import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users, Trophy, Building2, Zap, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import api from '../services/api';

const GlobalSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Suggestions as user types
  useEffect(() => {
    if (query.length < 1) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggestions`, {
          params: { q: query }
        });
        setSuggestions(data.suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSearch = async () => {
    if (query.length < 2) return;
    
    setLoading(true);
    setShowSuggestions(false);
    
    try {
      const { data } = await api.get(`/search/global`, {
        params: { q: query }
      });
      setResults(data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleResultClick = (item) => {
    switch (item.type) {
      case 'match':
        navigate(`/matches/${item.id}`);
        break;
      case 'player':
        navigate(`/player/${item.id}`);
        break;
      case 'tournament':
        navigate(`/tournaments/${item.id}`);
        break;
      case 'club':
        navigate(`/clubs/${item.id}`);
        break;
      default:
        break;
    }
    onClose();
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.text);
    setShowSuggestions(false);
    setTimeout(handleSearch, 100);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'match': return <Zap size={16} className="text-yellow-400" />;
      case 'player': return <Users size={16} className="theme-accent" />;
      case 'tournament': return <Trophy size={16} className="text-orange-400" />;
      case 'club': return <Building2 size={16} className="text-purple-400" />;
      default: return null;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'match': return 'Match';
      case 'player': return 'Joueur';
      case 'tournament': return 'Tournoi';
      case 'club': return 'Club';
      default: return type;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 backdrop-blur-sm ${isDark ? 'bg-black/80' : 'bg-white/80'}`}>
      <div className="max-w-2xl mx-auto pt-20 px-4">
        {/* Search Header */}
        <div className="relative">
          <div className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
            isDark 
              ? 'bg-zinc-900 border-zinc-700' 
              : 'bg-white border-gray-300 shadow-lg'
          }`}>
            <Search size={20} className="theme-text-secondary" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Rechercher matchs, joueurs, tournois, clubs..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className={`flex-1 bg-transparent border-0 placeholder:theme-text-secondary focus-visible:ring-0 text-lg ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}
              data-testid="global-search-input"
            />
            {loading ? (
              <Loader2 size={20} className="theme-accent animate-spin" />
            ) : query && (
              <button onClick={() => setQuery('')}>
                <X size={20} className="theme-text-secondary hover:theme-text" />
              </button>
            )}
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && !results && (
            <div className={`absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden border ${
              isDark 
                ? 'bg-zinc-900 border-zinc-700' 
                : 'bg-white border-gray-300 shadow-lg'
            }`}>
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    isDark ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'
                  }`}
                >
                  <span className="text-lg">{suggestion.icon}</span>
                  <span className="theme-text">{suggestion.text}</span>
                  <span className="text-xs theme-text-secondary ml-auto">{getTypeLabel(suggestion.type)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Close hint */}
        <div className="text-center mt-3 theme-text-secondary text-sm">
          Appuyez sur <kbd className={`px-2 py-1 rounded text-xs ${isDark ? 'bg-zinc-800' : 'bg-gray-200'}`}>ESC</kbd> pour fermer
        </div>

        {/* Results */}
        {results && (
          <div className="mt-6 space-y-4 max-h-[60vh] overflow-y-auto">
            {results.totalResults === 0 ? (
              <div className="text-center py-8 theme-text-secondary">
                <Search size={48} className="mx-auto mb-3 opacity-50" />
                <p>Aucun résultat pour "{query}"</p>
              </div>
            ) : (
              <>
                {/* Matches */}
                {results.matches.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold theme-text-secondary mb-2 flex items-center gap-2">
                      <Zap size={14} /> Matchs ({results.matches.length})
                    </h3>
                    <div className="space-y-2">
                      {results.matches.map(match => (
                        <button
                          key={match.id}
                          onClick={() => handleResultClick(match)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                            isDark 
                              ? 'bg-zinc-900/50 border-zinc-800 hover:border-yellow-500/50' 
                              : 'bg-white border-gray-200 hover:border-yellow-500 shadow-sm'
                          }`}
                          data-testid={`search-result-${match.id}`}
                        >
                          {getTypeIcon('match')}
                          <div className="flex-1">
                            <div className="theme-text font-medium">{match.title}</div>
                            <div className="text-sm theme-text-secondary">{match.subtitle}</div>
                          </div>
                          <span className="text-xs bg-yellow-500/20 text-yellow-600 px-2 py-1 rounded">
                            {match.playersCount}/{match.maxPlayers}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Players */}
                {results.players.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold theme-text-secondary mb-2 flex items-center gap-2">
                      <Users size={14} /> Joueurs ({results.players.length})
                    </h3>
                    <div className="space-y-2">
                      {results.players.map(player => (
                        <button
                          key={player.id}
                          onClick={() => handleResultClick(player)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                            isDark 
                              ? 'bg-zinc-900/50 border-zinc-800 hover:border-[#00d4ff]/50' 
                              : 'bg-white border-gray-200 hover:border-[#00d4ff] shadow-sm'
                          }`}
                        >
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center text-black font-bold"
                            style={{ backgroundColor: player.avatarColor }}
                          >
                            {player.title.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <div className="theme-text font-medium">{player.title}</div>
                            <div className="text-sm theme-text-secondary">{player.subtitle}</div>
                          </div>
                          <span className="text-xs bg-[#00d4ff]/20 theme-accent px-2 py-1 rounded">
                            ⭐ {player.rating}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tournaments */}
                {results.tournaments.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold theme-text-secondary mb-2 flex items-center gap-2">
                      <Trophy size={14} /> Tournois ({results.tournaments.length})
                    </h3>
                    <div className="space-y-2">
                      {results.tournaments.map(tournament => (
                        <button
                          key={tournament.id}
                          onClick={() => handleResultClick(tournament)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                            isDark 
                              ? 'bg-zinc-900/50 border-zinc-800 hover:border-orange-500/50' 
                              : 'bg-white border-gray-200 hover:border-orange-500 shadow-sm'
                          }`}
                        >
                          {getTypeIcon('tournament')}
                          <div className="flex-1">
                            <div className="theme-text font-medium">{tournament.title}</div>
                            <div className="text-sm theme-text-secondary">{tournament.subtitle}</div>
                          </div>
                          <span className="text-xs bg-orange-500/20 text-orange-500 px-2 py-1 rounded">
                            {tournament.teamsCount}/{tournament.maxTeams} équipes
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Clubs */}
                {results.clubs.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold theme-text-secondary mb-2 flex items-center gap-2">
                      <Building2 size={14} /> Clubs ({results.clubs.length})
                    </h3>
                    <div className="space-y-2">
                      {results.clubs.map(club => (
                        <button
                          key={club.id}
                          onClick={() => handleResultClick(club)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                            isDark 
                              ? 'bg-zinc-900/50 border-zinc-800 hover:border-purple-500/50' 
                              : 'bg-white border-gray-200 hover:border-purple-500 shadow-sm'
                          }`}
                        >
                          {getTypeIcon('club')}
                          <div className="flex-1">
                            <div className="theme-text font-medium">{club.title}</div>
                            <div className="text-sm theme-text-secondary">{club.subtitle}</div>
                          </div>
                          <span className="text-xs bg-purple-500/20 text-purple-500 px-2 py-1 rounded">
                            {club.courtsCount} courts
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Close button */}
        <button
          onClick={onClose}
          className="fixed top-4 right-4 p-2 theme-text-secondary hover:theme-text transition-colors"
        >
          <X size={24} />
        </button>
      </div>
    </div>
  );
};

export default GlobalSearch;
